// Stage 17: Homunculus Module — frontend API client.
//
// Mirrors frontend/src/api/memoryAdv.ts in style. The operatorForget
// call is deliberately isolated in this module; no other component may
// import it — the structural guard that keeps the operation invisible
// to the model per TINS Addendum A5.

import { apiGet, apiJson } from './client';

export type EngramType =
  | 'pocket-recognition'
  | 'closeout-warning'
  | 'trajectory-template'
  | 'wipeout-residue'
  | 'calibration-snapshot';

// Stage 19: corrected to match the backend's hydrated Engram shape
// (see worker/src/homunculus/pipelines/hydrate-engram.ts). The API
// returns camelCase with nested `pattern` + `fieldAnchor` objects,
// NOT the raw D1 row's snake_case flat columns. The previous
// snake-case interface caused every field access to return undefined
// at runtime → inspector crash in HomunculusTab's row map.
export interface HomunculusFieldSignature {
  dimensions: string[];
  geometry: string;
  energyGradient: string;
}

export interface HomunculusLineSignature {
  commitmentStyle: string;
  correctionTolerance: string;
  lookahead: string;
}

export interface HomunculusOutcomeSignature {
  accuracy: number;
  convergence: string;
  impossibilityIndexDelta: number;
}

export interface HomunculusCMACPattern {
  fieldSignature: HomunculusFieldSignature;
  lineSignature: HomunculusLineSignature;
  outcomeSignature: HomunculusOutcomeSignature;
  phenomenalTag: string;
}

export interface HomunculusFieldAnchor {
  observedIn: string[];
  failedIn: string[];
  neutralIn: string[];
}

export interface HomunculusEngram {
  id: string;
  type: EngramType;
  shapeKey: string | null;
  pattern: HomunculusCMACPattern;
  confidence: number;
  sessionId: string;
  authoredAt: number; // unixepoch seconds
  fieldAnchor: HomunculusFieldAnchor;
  supersededBy: string | null;
  forgottenAt: number | null;
  searchQueries: string[];
}

export interface HomunculusHealth {
  ok: boolean;
  service: string;
  version: string;
  region: string;
  timestamp: string;
}

export interface HomunculusWakeCalibration {
  lastKnownAccuracy: number | null;
  trendVector: 'upward' | 'plateauing' | 'declining';
  sessionsSinceLastWipeout: number;
  fidelityRecommendation: number;
  impossibilityIndexAtStart: number | null;
  lastLoopAmplification: number | null;
  lastCeilingEstimate: 'low' | 'high' | 'incomprehensible' | null;
  biasFlags: string[];
}

export interface HomunculusWakeResponse {
  wakeId: string;
  residue: {
    pocketRecognitions: HomunculusEngram[];
    closeoutWarnings: HomunculusEngram[];
    trajectoryTemplates: HomunculusEngram[];
    recentWipeouts: HomunculusEngram[];
  };
  calibration: HomunculusWakeCalibration;
  closeoutMap: {
    zones: Array<{ signature: unknown; hits: number }>;
  };
  durationMs: number;
}

// Public (no auth) — worker healthcheck for the tab's reachability badge.
export async function healthcheck(apiBase: string): Promise<HomunculusHealth> {
  const res = await fetch(`${apiBase}/api/homunculus/healthcheck`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as HomunculusHealth;
}

// ── Read-side ──
export async function listResidue(params: {
  type?: EngramType | 'all';
  sessionId?: string;
  seedSource?: 'self' | 'corpus' | 'external_mind' | 'digital_twin' | 'composite';
  includeSuperseded?: boolean;
  includeForgotten?: boolean;
  cursor?: string;
  limit?: number;
}): Promise<{ engrams: HomunculusEngram[]; nextCursor: string | null }> {
  const q = new URLSearchParams();
  if (params.type && params.type !== 'all') q.set('type', params.type);
  if (params.sessionId) q.set('sessionId', params.sessionId);
  if (params.seedSource) q.set('seedSource', params.seedSource);
  if (params.includeSuperseded) q.set('includeSuperseded', 'true');
  if (params.includeForgotten) q.set('includeForgotten', 'true');
  if (params.cursor) q.set('cursor', params.cursor);
  if (params.limit) q.set('limit', String(params.limit));
  return apiGet(`/api/homunculus/residue?${q.toString()}`);
}

// ── Harness operations (admin-triggered from the tab) ──
export const wakeNow = (sessionId: string) =>
  apiJson<HomunculusWakeResponse>('POST', '/api/homunculus/wake', {
    sessionId,
  });

export const sleepNow = (sessionId: string) =>
  apiJson<{
    sleepId: string;
    engramsCommitted: number;
    wipeoutsRecorded: number;
    nextWakeContextSize: number;
    durationMs: number;
  }>('POST', '/api/homunculus/sleep', {
    sessionId,
    // Server reconstructs trajectory from turns if empty.
    trajectoryLog: [],
    finalCalibration: {
      predictionAccuracy: 0.5,
      growthVector: 'plateauing',
      impossibilityIndexEnd: 0,
    },
  });

// ── Operator-only (requires HOMUNCULUS_OPERATOR_TOKEN) ──
// DO NOT re-export this from any shared-UI surface. Only the operator
// region of HomunculusTab may call it.
export const operatorForget = (
  engramId: string,
  reason: string,
  operatorToken: string,
) =>
  apiJson<{ forgotten: boolean; engramId: string }>(
    'POST',
    '/api/homunculus/forget',
    { engramId, reason },
    { extraHeaders: { 'X-Operator-Token': operatorToken } },
  );

// ── URLs the tab opens in new windows ──
export const exportUrl = (base: string) =>
  `${base}/api/homunculus/export?format=ndjson`;

export const sessionReportUrl = (
  base: string,
  sessionId: string,
  format: 'json' | 'markdown' = 'json',
) =>
  `${base}/api/homunculus/session-report?sessionId=${encodeURIComponent(sessionId)}&format=${format}`;
